import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import Button from '@/components/ui/Button';
import { getDataType } from '../catalog';
import { PointView } from '../selectors';
import { DataSource, IntegrationPointStatus } from '../types';
import SourceSelect, { SourceChoice } from './SourceSelect';

const statusTone: Record<IntegrationPointStatus, string> = {
  connected: 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/25 dark:text-green-300',
  native: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/25 dark:text-blue-300',
  notSet: 'border-border bg-muted/50 text-muted-foreground',
  notTracked: 'border-border bg-transparent text-muted-foreground',
  sourceError: 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/25 dark:text-red-300',
  notOffered: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/25 dark:text-amber-300',
};

interface IntegrationPointRowProps {
  view: PointView;
  sources: DataSource[];
  onChoose: (choice: SourceChoice) => void;
  onOpenMapping: () => void;
  onOpenPreview: () => void;
}

const IntegrationPointRow: React.FC<IntegrationPointRowProps> = ({
  view, sources, onChoose, onOpenMapping, onOpenPreview,
}) => {
  const { t } = useTranslation();
  const def = getDataType(view.point.dataType);
  const { status } = view;

  const lastUpdated = status === 'notTracked'
    ? t('extInt.points.notApplicable')
    : status === 'native'
      ? t('extInt.points.live')
      : view.point.lastUpdatedOn
        ? new Date(view.point.lastUpdatedOn).toLocaleString()
        : t('extInt.points.never');

  const canActOnSource = status === 'connected';

  return (
    <div
      className="grid grid-cols-1 items-center gap-3 border-t border-border px-3 py-3 sm:grid-cols-[2fr_1.4fr_1fr_1fr]"
      data-testid={`point-row-${view.point.dataType}`}
    >
      <div className="min-w-0">
        <Tooltip content={t(def.descriptionKey)}>
          <span className="text-sm font-medium text-foreground">{t(def.labelKey)}</span>
        </Tooltip>
        {def.foundational && (
          <p className="mt-0.5 text-xs text-muted-foreground">{t('extInt.points.foundational')}</p>
        )}
      </div>

      <SourceSelect
        sources={sources}
        owner={view.point.owner}
        sourceId={view.point.sourceId}
        onChange={onChoose}
        data-testid={`point-source-${view.point.dataType}`}
      />

      <div>
        <span
          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusTone[status]}`}
          data-testid={`point-status-${view.point.dataType}`}
        >
          {t(`extInt.pointStatus.${status}`)}
        </span>
        {status === 'notOffered' && (
          <p className="mt-1 text-xs text-muted-foreground">{t('extInt.pointStatus.notOffered.hint')}</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground" data-testid={`point-updated-${view.point.dataType}`}>
          {lastUpdated}
        </span>
        {canActOnSource && (
          <span className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onOpenMapping} data-testid={`point-mapping-${view.point.dataType}`}>
              {t('extInt.action.mapping')}
            </Button>
            <Button size="sm" variant="outline" onClick={onOpenPreview} data-testid={`point-preview-${view.point.dataType}`}>
              {t('extInt.action.preview')}
            </Button>
          </span>
        )}
      </div>
    </div>
  );
};

export default IntegrationPointRow;
